import { lazy, Suspense, type ComponentType } from 'react';
import { RequireAuth } from '@greensm/auth';
import { CardSkeleton } from '@greensm/ui';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';
const AccountPage = deferredPage(() => import('@/features/auth/AccountPage').then(m => ({ default: m.AccountPage })));
const LoginPage = deferredPage(() => import('@/features/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const BookingAssistantPage = deferredPage(() => import('@/features/assistant-booking/BookingAssistantPage').then(m => ({ default: m.BookingAssistantPage })));
const VerifyPhonePage = deferredPage(() => import('@/features/auth/VerifyPhonePage').then(m => ({ default: m.VerifyPhonePage })));
const EditAddressPage = deferredPage(() => import('@/features/booking/EditAddressPage').then(m => ({ default: m.EditAddressPage })));
const DestinationMapPage = deferredPage(() => import('@/features/booking/DestinationMapPage').then(m => ({ default: m.DestinationMapPage })));
const PendingPage = deferredPage(() => import('@/features/booking/PendingPage').then(m => ({ default: m.PendingPage })));
const PickupPage = deferredPage(() => import('@/features/booking/PickupPage').then(m => ({ default: m.PickupPage })));
const QuotePage = deferredPage(() => import('@/features/booking/QuotePage').then(m => ({ default: m.QuotePage })));
const SearchPage = deferredPage(() => import('@/features/booking/SearchPage').then(m => ({ default: m.SearchPage })));
const PlanPage = deferredPage(() => import('@/features/change-plan/PlanPage').then(m => ({ default: m.PlanPage })));
const HomePage = deferredPage(() => import('@/features/home/HomePage').then(m => ({ default: m.HomePage })));
const NotificationsPage = deferredPage(() => import('@/features/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const MyRequestsPage = deferredPage(() => import('@/features/support-sos/MyRequestsPage').then(m => ({ default: m.MyRequestsPage })));
const NewRequestPage = deferredPage(() => import('@/features/support-sos/NewRequestPage').then(m => ({ default: m.NewRequestPage })));
const SupportCategoryPage = deferredPage(() => import('@/features/support-sos/SupportCategoryPage').then(m => ({ default: m.SupportCategoryPage })));
const SupportCenterPage = deferredPage(() => import('@/features/support-sos/SupportCenterPage').then(m => ({ default: m.SupportCenterPage })));
const TicketPage = deferredPage(() => import('@/features/support-sos/TicketPage').then(m => ({ default: m.TicketPage })));
const HistoryPage = deferredPage(() => import('@/features/trip-history/HistoryPage').then(m => ({ default: m.HistoryPage })));
const TripDetailPage = deferredPage(() => import('@/features/trip-history/TripDetailPage').then(m => ({ default: m.TripDetailPage })));
const TripInfoPage = deferredPage(() => import('@/features/trip-history/TripDocPages').then(m => ({ default: m.TripInfoPage })));
const TripPaymentPage = deferredPage(() => import('@/features/trip-history/TripDocPages').then(m => ({ default: m.TripPaymentPage })));
const TripReceiptPage = deferredPage(() => import('@/features/trip-history/TripDocPages').then(m => ({ default: m.TripReceiptPage })));
const TripInsurancePage = deferredPage(() => import('@/features/trip-history/TripExtraPages').then(m => ({ default: m.TripInsurancePage })));
const TripInvoicePage = deferredPage(() => import('@/features/trip-history/TripExtraPages').then(m => ({ default: m.TripInvoicePage })));
const TripPage = deferredPage(() => import('@/features/trip-tracking/TripPage').then(m => ({ default: m.TripPage })));
const CurrentTripRedirect = deferredPage(() => import('@/features/trip-tracking/v3/TripPageV3').then(m => ({ default: m.CurrentTripRedirect })));
const TripPageV3 = deferredPage(() => import('@/features/trip-tracking/v3/TripPageV3').then(m => ({ default: m.TripPageV3 })));
import { AppFrame } from './layouts/AppFrame';
import { AppShell } from './layouts/AppShell';


function deferredPage(loader: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(loader);
  return function DeferredPage() {
    return <Suspense fallback={<div role="status" style={{padding: 16}}><CardSkeleton count={2}/></div>}><Page/></Suspense>;
  };
}

export const routes: RouteObject[] = [
  {
    element: <AppFrame />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/login/verify-phone', element: <VerifyPhonePage /> }, // S03
      {
        element: <RequireAuth fallback={<div style={{ padding: 16 }}><CardSkeleton count={2} /></div>} />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: '/', element: <HomePage /> },
              { path: '/assistant', element: <BookingAssistantPage /> }, // C2 v3: đặt xe cùng trợ lý (FE-3.1)
              { path: '/book', element: <SearchPage /> },
              { path: '/book/destination-map', element: <DestinationMapPage /> },
              { path: '/book/quote', element: <QuotePage /> },
              { path: '/book/pickup', element: <PickupPage /> },
              { path: '/book/edit-address', element: <EditAddressPage /> },
              { path: '/book/pending', element: <PendingPage /> },
              { path: '/trips/:tripId', element: <TripPage /> },   // FE-PC03 (v1 – luồng đặt xe cũ vẫn dùng tới FE-3.1)
              { path: '/ride', element: <CurrentTripRedirect /> }, // C4 v3: chuyến đang diễn ra
              { path: '/ride/:tripId', element: <TripPageV3 /> },  // C4 v3 (FE-2.5)
              { path: '/plans/:planId', element: <PlanPage /> },   // FE-PC04
              { path: '/history', element: <HistoryPage /> },      // FE-PC05
              { path: '/history/:tripId', element: <TripDetailPage /> },
              { path: '/history/:tripId/info', element: <TripInfoPage /> },       // S12
              { path: '/history/:tripId/payment', element: <TripPaymentPage /> }, // S13
              { path: '/history/:tripId/receipt', element: <TripReceiptPage /> }, // S14
              { path: '/history/:tripId/invoice', element: <TripInvoicePage /> }, // S15
              { path: '/history/:tripId/insurance', element: <TripInsurancePage /> }, // S16
              { path: '/support', element: <SupportCenterPage /> },            // S18
              { path: '/support/sos', element: <Navigate to="/support" replace /> },                  // FE-PC06
              { path: '/support/category', element: <SupportCategoryPage /> }, // S19
              { path: '/support/new', element: <NewRequestPage /> },
              { path: '/support/requests', element: <MyRequestsPage /> },      // S20
              { path: '/support/requests/:ticketId', element: <TicketPage /> },
              { path: '/notifications', element: <NotificationsPage /> }, // FE-PC07
              { path: '/account', element: <AccountPage /> },      // FE-PC01
              { path: '*', element: <HomePage /> },
            ],
          },
        ],
      },
    ],
  },
];

/** Cây route của app. Đặt trong <BrowserRouter> (chạy thật) hoặc <MemoryRouter> (test). */
export function AppRoutes() {
  return useRoutes(routes);
}
